import { useState } from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import {
  Loader2,
  RefreshCw,
  RotateCcw,
  Gauge,
  Database,
  AlertTriangle,
  ChevronDown,
  ChevronRight as ChevronRightSmall,
} from "lucide-react";
import { toast } from "sonner";

import { GET_PERF_LOGS } from "@/graphql/queries/perf";
import { RESET_PERF_LOGS, COPY_PERF_REPORT } from "@/graphql/mutations/perf";
import { CopyForAi } from "@/components/admin/copy-for-ai";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const PAGE_SIZE = 50;

type PerfRow = {
  id: string;
  label: string;
  kind: string;
  requests: number;
  totalMs: number;
  avgMs: number;
  maxMs: number;
  avgDbMs: number;
  appMs: number;
  avgQueries: number;
  maxQueries: number;
  slowCount: number;
  verySlowCount: number;
  errorCount: number;
  slowShare: number;
  worstAt: string | null;
  worstContext: Record<string, unknown> | null;
  firstSeenAt: string;
  lastSeenAt: string;
  copiedAt: string | null;
};

function ms(value: number): string {
  if (value >= 10000) return `${(value / 1000).toFixed(1)} s`;
  if (value >= 100) return `${Math.round(value)} ms`;
  return `${value.toFixed(1)} ms`;
}

function absolute(iso: string): string {
  return new Intl.DateTimeFormat("lt-LT", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(iso));
}

/**
 * Našumas.
 *
 * Ordered by total time spent rather than by the slowest single request: the
 * thing worth fixing is what the app spends its life doing, and a `max` ordering
 * puts one outlier above an operation quietly burning ten times as much across a
 * thousand calls.
 */
export default function PerformancePage() {
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState("cost");
  const [kind, setKind] = useState("all");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery(GET_PERF_LOGS, {
    variables: {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      sort,
      kind: kind === "all" ? undefined : kind,
      search: search.trim() || undefined,
    },
    fetchPolicy: "cache-and-network",
  });

  const [resetPerfLogs, { loading: isResetting }] = useMutation(RESET_PERF_LOGS);

  // What the page is showing, and therefore what a copy is of: the paste and the
  // list must describe the same measurements. How much of it the report holds is
  // stated in the report itself.
  const shown = {
    sort,
    kind: kind === "all" ? undefined : kind,
    search: search.trim() || undefined,
  };

  const logs: PerfRow[] = (data as any)?.perfLogs?.logs ?? [];
  const total = (data as any)?.perfLogs?.total ?? 0;
  const stats = (data as any)?.perfLogs?.stats ?? null;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const grandTotalMs = stats?.totalMs ?? 0;

  const reset = async () => {
    try {
      const result: any = await resetPerfLogs({ variables: { scope: "all" } });
      const errors = result?.data?.resetPerfLogs?.errors ?? [];
      if (errors.length) {
        toast.error(errors[0]);
        return;
      }
      toast.success("Skaitliukai išvalyti — matavimas prasideda iš naujo");
      refetch();
    } catch {
      toast.error("Nepavyko išvalyti");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Našumas</h1>
          <p className="text-muted-foreground">
            Kur programa praleidžia laiką. Viena eilutė — viena užklausos rūšis, ne
            vienas atvejis
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/*
            The whole point of the table: hand the numbers to somebody who can
            act on them. The markdown is built on the server so this button and
            `rake perf:report` cannot describe the same measurements differently
            — and it is a mutation, because what went is stamped `copiedAt`.
          */}
          <CopyForAi
            document={COPY_PERF_REPORT}
            pick={(data) => data?.copyPerfReport}
            variables={{ limit: 25, ...shown }}
            label="Kopijuoti AI"
            hint="Rodomas operacijas su skaičiais — įklijuok pokalbyje su AI"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Atnaujinti
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" disabled={isResetting}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Pradėti iš naujo
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Išvalyti skaitliukus?</AlertDialogTitle>
                <AlertDialogDescription>
                  Visi skaičiai kaupiasi nuo matavimo pradžios, tad šiandien
                  pataisytas dalykas dar ilgai skęsta senuose vidurkiuose. Išvalius
                  matavimas prasideda nuo kitos užklausos — patogu norint pamatyti,
                  ar taisymas suveikė.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Atšaukti</AlertDialogCancel>
                <AlertDialogAction onClick={reset}>Išvalyti</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Užklausų</div>
            <div className="text-2xl font-bold">{stats.totalRequests}</div>
            <div className="text-xs text-muted-foreground">
              {stats.trackedSince
                ? `nuo ${absolute(stats.trackedSince)}`
                : "dar nieko"}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Vidutiniškai</div>
            <div className="text-2xl font-bold">{ms(stats.avgMs)}</div>
            <div className="text-xs text-muted-foreground">
              iš jų DB{" "}
              {stats.totalRequests > 0
                ? ms(stats.totalDbMs / stats.totalRequests)
                : "—"}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Lėtų</div>
            <div
              className={`text-2xl font-bold ${
                stats.verySlowRequests > 0 ? "text-red-600" : ""
              }`}
            >
              {stats.slowRequests}
            </div>
            <div className="text-xs text-muted-foreground">
              &gt;{Math.round(stats.slowMs)} ms · iš jų {stats.verySlowRequests} virš{" "}
              {Math.round(stats.verySlowMs / 1000)} s
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Nulūžusių</div>
            <div
              className={`text-2xl font-bold ${
                stats.failedRequests > 0 ? "text-red-600" : ""
              }`}
            >
              {stats.failedRequests}
            </div>
            <div className="text-xs text-muted-foreground">
              saugoma {stats.retentionDays} d. po paskutinio karto
            </div>
          </Card>
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="w-56">
              <Select
                value={sort}
                onValueChange={(value) => {
                  setSort(value);
                  setPage(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cost">Daugiausiai laiko iš viso</SelectItem>
                  <SelectItem value="slowest">Lėčiausias vienas kartas</SelectItem>
                  <SelectItem value="queries">Daugiausiai DB užklausų</SelectItem>
                  <SelectItem value="recent">Naujausios</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-44">
              <Select
                value={kind}
                onValueChange={(value) => {
                  setKind(value);
                  setPage(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Viskas</SelectItem>
                  <SelectItem value="graphql">GraphQL</SelectItem>
                  <SelectItem value="http">HTTP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[16rem] flex-1">
              <Input
                placeholder="Ieškoti operacijos…"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Užklausų rūšys</CardTitle>
          <CardDescription>
            Rodoma {logs.length} iš {total}. Juosta — kokią viso laiko dalį suėda ta
            operacija
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && logs.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <div className="py-12 text-center">
              <Gauge className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
              <div className="font-medium">Dar nieko neužfiksuota</div>
              <div className="text-sm text-muted-foreground">
                Skaičiai atsiras, kai kas nors pasinaudos programa
              </div>
            </div>
          ) : (
            <>
              <div className="divide-y">
                {logs.map((log) => {
                  const isOpen = openId === log.id;
                  const share =
                    grandTotalMs > 0 ? (log.totalMs / grandTotalMs) * 100 : 0;
                  // An average of a handful of queries with a worst case in the
                  // hundreds is the exact shape of a loop issuing one query per
                  // item — the finding that time alone cannot show, because on a
                  // local SQLite file 500 queries can still be fast.
                  const looksLikeNPlusOne =
                    log.maxQueries >= 50 && log.maxQueries > log.avgQueries * 3;

                  return (
                    <div key={log.id} className="py-3">
                      <div className="flex items-start gap-3">
                        <button
                          type="button"
                          onClick={() => setOpenId(isOpen ? null : log.id)}
                          className="mt-0.5 text-muted-foreground hover:text-foreground"
                          aria-label={isOpen ? "Suskleisti" : "Išskleisti"}
                        >
                          {isOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRightSmall className="h-4 w-4" />
                          )}
                        </button>

                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-mono text-sm font-semibold">
                              {log.label}
                            </span>
                            <Badge variant="secondary" className="text-xs">
                              {log.requests}×
                            </Badge>
                            {log.verySlowCount > 0 && (
                              <Badge variant="destructive" className="text-xs">
                                <AlertTriangle className="mr-1 h-3 w-3" />
                                {log.verySlowCount}× virš 2 s
                              </Badge>
                            )}
                            {looksLikeNPlusOne && (
                              <Badge
                                variant="outline"
                                className="border-amber-300 text-amber-700 text-xs"
                              >
                                <Database className="mr-1 h-3 w-3" />
                                galimas N+1
                              </Badge>
                            )}
                            {log.errorCount > 0 && (
                              <Badge variant="destructive" className="text-xs">
                                {log.errorCount}× nulūžo
                              </Badge>
                            )}
                          </div>

                          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                            <span>
                              vid. <strong className="text-foreground">{ms(log.avgMs)}</strong>
                            </span>
                            <span>max {ms(log.maxMs)}</span>
                            <span>DB {ms(log.avgDbMs)}</span>
                            <span>
                              {log.avgQueries.toFixed(1)} užkl.
                              {log.maxQueries > log.avgQueries + 1 && (
                                <> (max {log.maxQueries})</>
                              )}
                            </span>
                            <span>viso {(log.totalMs / 1000).toFixed(1)} s</span>
                          </div>

                          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full bg-primary"
                              style={{ width: `${Math.min(100, share)}%` }}
                            />
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-1">
                          <div className="w-16 text-right text-sm font-medium tabular-nums">
                            {share.toFixed(1)}%
                          </div>
                          {/* Icon-only and always visible: the hover is where
                              "already handed over" is read, so it must not be
                              behind expanding the row. */}
                          <CopyForAi
                            document={COPY_PERF_REPORT}
                            pick={(data) => data?.copyPerfReport}
                            variables={{ id: log.id }}
                            hint="Kopijuoti AI — šią operaciją su lėčiausiu kartu"
                            copiedAt={log.copiedAt}
                            variant="ghost"
                            size="xs"
                          />
                        </div>
                      </div>

                      {isOpen && (
                        <div className="space-y-3 pl-7 pt-3 text-sm">
                          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                            <span>
                              Ruby {ms(log.appMs / Math.max(1, log.requests))} vid.
                            </span>
                            <span>
                              Lėtų: {log.slowCount} (
                              {(log.slowShare * 100).toFixed(0)}%)
                            </span>
                            <span>Pirmą kartą: {absolute(log.firstSeenAt)}</span>
                            <span>Paskutinį kartą: {absolute(log.lastSeenAt)}</span>
                          </div>

                          {log.worstAt && (
                            <div>
                              <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                                Lėčiausias kartas ({absolute(log.worstAt)})
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(log.worstContext ?? {}).map(
                                  ([key, value]) => (
                                    <Badge
                                      key={key}
                                      variant="outline"
                                      className="font-mono text-xs"
                                    >
                                      {key}: {String(value)}
                                    </Badge>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Puslapis {page + 1} iš {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      Ankstesnis
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                    >
                      Kitas
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
